import { useCallback, useEffect, useRef, useState } from 'react';
import { pb } from '../lib/pocketbase.js';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_MS = 2 * 60 * 1000;
const ACTIVITY_DEBOUNCE_MS = 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * Auto-Logout nach Inaktivität.
 *
 * - Bei jedem User-Event (debounced auf 1s) startet der Inaktivitäts-Timer neu.
 * - 28 Min ohne Aktivität ⇒ Warning-Modal mit Countdown.
 * - 30 Min ohne Aktivität ⇒ Logout (authStore.clear() + onLogout-Callback).
 * - Listener und Timer laufen nur, solange `enabled` true ist (= eingeloggt).
 *
 * Während das Warning-Modal offen ist, werden User-Events bewusst NICHT als
 * "Aktivität" gewertet — sonst würde der erste Mausschubser das Modal direkt
 * wieder schließen. Der User muss explizit auf einen Button klicken.
 */
export function useInactivityLogout({ enabled, onLogout }) {
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_BEFORE_MS / 1000));

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(0);
  const warningOpenRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const doLogout = useCallback(() => {
    clearTimers();
    warningOpenRef.current = false;
    setWarningOpen(false);
    // authStore.clear() ist idempotent — mehrfacher Aufruf schadet nicht.
    pb.authStore.clear();
    onLogout?.();
  }, [clearTimers, onLogout]);

  const startCountdown = useCallback(() => {
    warningOpenRef.current = true;
    setWarningOpen(true);
    setSecondsLeft(Math.floor(WARNING_BEFORE_MS / 1000));

    countdownIntervalRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    logoutTimerRef.current = setTimeout(doLogout, WARNING_BEFORE_MS);
  }, [doLogout]);

  const resetTimer = useCallback(() => {
    clearTimers();
    warningOpenRef.current = false;
    setWarningOpen(false);
    warningTimerRef.current = setTimeout(
      startCountdown,
      INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS,
    );
  }, [clearTimers, startCountdown]);

  // Vom Modal aufgerufen, wenn der User "Eingeloggt bleiben" klickt.
  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      warningOpenRef.current = false;
      setWarningOpen(false);
      return undefined;
    }

    const handleActivity = () => {
      // Während das Warning offen ist, ignorieren wir Hintergrund-Events —
      // der User muss explizit reagieren.
      if (warningOpenRef.current) return;
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_DEBOUNCE_MS) return;
      lastActivityRef.current = now;
      resetTimer();
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, handleActivity, { passive: true });
    }

    resetTimer();

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, handleActivity);
      }
      clearTimers();
    };
  }, [enabled, resetTimer, clearTimers]);

  return {
    warningOpen,
    secondsLeft,
    dismissWarning,
    logoutNow: doLogout,
  };
}
